import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import { fetchAllTrainers } from 'src/models/trainer/trainer';
import { getUserDisplayName } from 'src/models/user/utils';

import {
  BaseSelect,
  BaseSelectOption,
  SelectProps,
  createFormSelect,
} from './BaseSelect';

type TrainerSelectProps = Omit<SelectProps, 'options' | 'loading'> & {
  multiple?: boolean;
  filterOptions?: (
    options: BaseSelectOption[],
    { inputValue }: { inputValue: string }
  ) => BaseSelectOption[];
  onInit?: (options: BaseSelectOption[]) => void;
};

export const TrainerSelect = ({
  multiple,
  filterOptions,
  onInit,
  ...props
}: TrainerSelectProps) => {
  const { sessionTrainer } = useSession();
  const initialRef = useRef(false);

  const { data: trainers, isLoading } = useQuery({
    queryKey: ['trainers_select', 'all'],
    staleTime: 60000,
    gcTime: 60000,
    queryFn: fetchAllTrainers,
  });

  const options: BaseSelectOption[] = (trainers || []).map((trainer) => ({
    id: trainer.trainerId,
    label: trainer.name,
    secondary: trainer.email,
    locationId: trainer.locationId,
  }));

  if (sessionTrainer && !options.some((o) => o.id === sessionTrainer.id)) {
    const trainerOption = {
      id: sessionTrainer.id,
      label: getUserDisplayName(sessionTrainer) || '',
      secondary: sessionTrainer.email,
      locationId: sessionTrainer.locationId,
    };
    options.unshift(trainerOption);
  }

  useEffect(() => {
    if (isLoading) return;
    if (options && !initialRef.current) {
      onInit?.(options);
      initialRef.current = true;
    }
  }, [onInit, options, isLoading]);

  return (
    <BaseSelect
      {...props}
      multiple={multiple}
      options={options}
      filterOptions={filterOptions}
      loading={isLoading}
    />
  );
};

export const FormTrainerSelect = createFormSelect(TrainerSelect);
